import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Check, FileDown, FileText, LoaderCircle, MoreVertical, Pencil, Save, Send, Trash2, Undo2, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { formatIsoDate } from '../../schedule/lib/format'
import { costApplicationStatusVariant } from '@/lib/status-variants'
import type { ApplicationAffordances } from '../lib/applications'
import { APPLICATION_STATUS_KEY } from '../lib/labels'
import type { CostApplication } from '../types'

interface Props {
  application: CostApplication
  af: ApplicationAffordances
  dirty: boolean
  saving: boolean
  /** i18n keys: why the step would be refused, or null. */
  submitBlocker: string | null
  certifyBlocker: string | null
  onBack: () => void
  onEdit: () => void
  onSave: () => void
  onSubmit: () => void
  onReturn: () => void
  onCertify: () => void
  onUncertify: () => void
  onInvoice: () => void
  onInvestorInvoice: () => void
  onRemoveInvestorInvoice: () => void
  /** The clear mutation is in flight. */
  removingInvoice?: boolean
  onDelete: () => void
  onProtocol: () => void
}

const ApplicationHeader: React.FC<Props> = ({ application, af, dirty, saving, submitBlocker, certifyBlocker, onBack, onEdit, onSave, onSubmit, onReturn, onCertify, onUncertify, onInvoice, onInvestorInvoice, onRemoveInvestorInvoice, removingInvoice, onDelete, onProtocol }) => {
  const { t } = useTranslation()
  const recorded = application.investorInvoice
  const returnBlocker = af.returnIt && dirty ? 'CostControl_application_save_first' : null
  const blocker = af.submit ? submitBlocker : af.certify ? certifyBlocker ?? returnBlocker : returnBlocker
  return (
    <div className="flex flex-wrap items-start gap-3">
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack} aria-label={t('CostControl_application_back')}>
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="truncate text-xl font-semibold">{t('CostControl_application_title', { number: application.number })}</h1>
          <Badge variant={costApplicationStatusVariant(application.status)}>{t(APPLICATION_STATUS_KEY[application.status])}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">{application.counterpartyName} · {application.contractName}</p>
        {recorded && (
          <p className="text-sm text-muted-foreground">
            {t('CostControl_application_investor_invoice_recorded', { number: recorded.number })} · {formatIsoDate(t, recorded.issueDate)}
            {recorded.dueDate && ` · ${t('CostControl_invoice_field_dueDate')} ${formatIsoDate(t, recorded.dueDate)}`}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {(af.editDraft || af.review) && (
          <Button variant="outline" size="sm" onClick={onSave} disabled={!dirty || saving}>
            {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t(af.review ? 'CostControl_application_save_review' : 'CostControl_application_save')}
          </Button>
        )}
        {af.submit && (
          <Button size="sm" onClick={onSubmit} disabled={!!submitBlocker || saving}>
            <Send className="h-4 w-4" />
            {t('CostControl_application_submit')}
          </Button>
        )}
        {af.certify && (
          <Button size="sm" onClick={onCertify} disabled={!!certifyBlocker || saving}>
            <Check className="h-4 w-4" />
            {t('CostControl_application_certify')}
          </Button>
        )}
        {af.returnIt && (
          <Button variant="outline" size="sm" onClick={onReturn} disabled={!!returnBlocker || saving}>
            <X className="h-4 w-4" />
            {t('CostControl_application_return')}
          </Button>
        )}
        {af.invoice && (
          <Button size="sm" onClick={onInvoice} disabled={saving}>
            <FileText className="h-4 w-4" />
            {t('CostControl_application_invoice')}
          </Button>
        )}
        {af.investorInvoice && !recorded && (
          <Button size="sm" onClick={onInvestorInvoice} disabled={saving}>
            <FileText className="h-4 w-4" />
            {t('CostControl_application_investor_invoice')}
          </Button>
        )}
        {af.investorInvoice && recorded && (
          <>
            <Button variant="outline" size="sm" onClick={onInvestorInvoice} disabled={saving}>
              <Pencil className="h-4 w-4" />
              {t('Common_edit')}
            </Button>
            <Button variant="outline" size="sm" onClick={onRemoveInvestorInvoice} disabled={saving}>
              {removingInvoice ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {t('CostControl_application_investor_invoice_remove')}
            </Button>
          </>
        )}
        {af.uncertify && (
          <Button variant="outline" size="sm" onClick={onUncertify} disabled={saving}>
            <Undo2 className="h-4 w-4" />
            {t('CostControl_application_uncertify')}
          </Button>
        )}
        {af.protocol && (
          <Button variant="outline" size="sm" onClick={onProtocol}>
            <FileDown className="h-4 w-4" />
            {t('CostControl_application_protocol')}
          </Button>
        )}
        {af.editDraft && (
          <Button variant="outline" size="sm" onClick={onEdit} disabled={saving}>
            <Pencil className="h-4 w-4" />
            {t('Common_edit')}
          </Button>
        )}
        {af.remove && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('CostControl_application_more')}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
                <Trash2 className="h-4 w-4" />
                {t('Common_delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {blocker && <p className="w-full pl-11 text-xs text-muted-foreground">{t(blocker)}</p>}
    </div>
  )
}
export default ApplicationHeader
